import React, { useState, useEffect, useCallback } from 'react'
import { Wallet } from '@/screens/Wallet'
import { Cards } from '@/screens/Cards'
import { BackendTokenContext } from '@/auth/BackendTokenContext'
import { Store } from '@/screens/Store'
import { Explore } from '@/screens/Explore'
import { Inventory } from '@/screens/Inventory'
import { AppAuthProvider } from '@/auth/AppAuthProvider'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '@/helpers/firebase'
import { useTonAddress } from '@tonconnect/ui-react'
import { documentPath as usersDocumentPath, User } from 'common/schema/users'
import { useFirestoreDocCache } from '../hooks/useFirestoreCache'
import '@/screens/Tabs.css'
import '@/screens/Tutorial.css'

interface TutorialStep {
  target: string
  title: string
  content: string
  position: 'top' | 'bottom' | 'left' | 'right'
  requiredTab?: string
}

const tutorialSteps: TutorialStep[] = [
  {
    target: '.pack-card.unopened', // Updated selector
    title: 'Free Pack!',
    content: 'Click to open it and reveal your first cards!',
    position: 'bottom',
    requiredTab: 'Inventory',
  },
  {
    target: '.tab-item:nth-child(1)', // Cards tab
    title: 'View Your Cards',
    content: "Now let's check your cards! Click on the Cards tab.",
    position: 'top',
    requiredTab: 'Inventory',
  },
  {
    target: '.cards-actions',
    title: 'Manage Your Cards',
    content:
      'Here you can view your unrevealed and revealed cards. Click on any card to reveal its true form!',
    position: 'bottom',
    requiredTab: 'Cards',
  },
  {
    target: '.forge-toggle',
    title: 'Forge Cards',
    content:
      'Click here to enter forge mode. You can combine multiple cards of the same rarity to create a higher rarity card!',
    position: 'bottom',
    requiredTab: 'Cards',
  },
  {
    target: '.section-btn',
    title: 'Mint NFTs',
    content:
      'Click fragment to mint its according NFT. Use more fragments to mint high level NFT!',
    position: 'bottom',
    requiredTab: 'Inventory',
  },
]

const Tutorial: React.FC<{
  userId: string
  onComplete: () => void
  activeTab: string
  onTabChange: (tab: string) => void
}> = ({ userId, onComplete, activeTab, onTabChange }) => {
  const [currentStep, setCurrentStep] = useState(0)

  useEffect(() => {
    const currentStepData = tutorialSteps[currentStep]
    if (
      currentStepData.requiredTab &&
      currentStepData.requiredTab !== activeTab
    ) {
      onTabChange(currentStepData.requiredTab)
    }
  }, [currentStep, activeTab, onTabChange])

  const calculatePosition = (target: string) => {
    const element = document.querySelector(target)
    if (!element) return { top: 0, left: 0, width: 0, height: 0 }

    const rect = element.getBoundingClientRect()
    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    }
  }

  const handleNext = async () => {
    if (currentStep === tutorialSteps.length - 1) {
      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, {
        firstTimeUser: false,
      })
      onComplete()
    } else {
      setCurrentStep((prev) => prev + 1)
    }
  }

  const calculateTooltipPosition = (target: string, position: string) => {
    const element = document.querySelector(target)
    if (!element) return { top: 20, left: 20 } // Default fallback position

    const rect = element.getBoundingClientRect()
    const tooltipWidth = 280 // Width of our tooltip
    const tooltipHeight = 120 // Approximate height of tooltip
    const padding = 10 // Padding from edges

    let top = 0
    let left = 0

    switch (position) {
      case 'bottom':
        top = rect.bottom + padding
        left = Math.min(window.innerWidth - (tooltipWidth / 2 + padding), left)
        break

      case 'top':
        top = Math.max(rect.top - tooltipHeight - padding * 6, padding)
        left = Math.min(window.innerWidth - (tooltipWidth / 2 + padding), left)
        break

      case 'left':
        top = rect.top + rect.height / 2
        left = Math.max(rect.left - tooltipWidth - padding, padding)
        break

      case 'right':
        top = rect.top + rect.height / 2
        left = rect.right + padding
        // Ensure tooltip doesn't go off-screen right
        if (left + tooltipWidth > window.innerWidth) {
          left = rect.left - tooltipWidth - padding
        }
        break
    }

    // Final safety checks to ensure tooltip is always on screen
    top = Math.max(
      padding,
      Math.min(window.innerHeight - tooltipHeight - padding, top)
    )
    left = Math.max(
      padding,
      Math.min(window.innerWidth - tooltipWidth - padding, left)
    )

    return { top, left }
  }

  // Calculate positions
  const currentHighlight = calculatePosition(tutorialSteps[currentStep].target)
  const tooltipPosition = calculateTooltipPosition(
    tutorialSteps[currentStep].target,
    tutorialSteps[currentStep].position
  )

  // Set CSS variables for the mask
  const overlayStyle = {
    '--highlight-x': `${currentHighlight.left}px`,
    '--highlight-y': `${currentHighlight.top}px`,
    '--highlight-width': `${currentHighlight.width}px`,
    '--highlight-height': `${currentHighlight.height}px`,
  } as React.CSSProperties

  return (
    <div className="tutorial-overlay" style={overlayStyle}>
      <div
        className="tutorial-highlight"
        style={{
          top: currentHighlight.top,
          left: currentHighlight.left,
          width: currentHighlight.width,
          height: currentHighlight.height,
        }}
      />
      <div
        className={`tutorial-tooltip tutorial-tooltip-${tutorialSteps[currentStep].position}`}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
        }}
      >
        <h3>{tutorialSteps[currentStep].title}</h3>
        <p>{tutorialSteps[currentStep].content}</p>
        <button onClick={handleNext} className="tutorial-button">
          {currentStep === tutorialSteps.length - 1 ? 'Finish' : 'Next'}
        </button>
      </div>
    </div>
  )
}

export const Tabs: React.FC = () => {
  const [activeTab, setActiveTab] = useState('Cards')
  const [token, setToken] = useState<string | null>(null)
  const [isFirstTime, setIsFirstTime] = useState<boolean>(false)
  const [isInTutorial, setIsInTutorial] = useState(false)

  const walletAddress = useTonAddress()
  const { data: userData } = useFirestoreDocCache<User>(
    walletAddress ? doc(db, usersDocumentPath(walletAddress)) : null,
    `userData-${walletAddress}`,
    (snapshot) => (snapshot.data() as User) || null,
    !!walletAddress
  )
  // Only check first time status when wallet address changes
  useEffect(() => {
    let isMounted = true

    const checkFirstTimeUser = async () => {
      if (!walletAddress) {
        if (isMounted) {
          setIsFirstTime(false)
          setIsInTutorial(false)
        }
        return
      }

      try {
        if (userData) {
          const isFirstTimeUser = userData.firstTimeUser ?? false
          setIsFirstTime(isFirstTimeUser)
          setIsInTutorial(isFirstTimeUser)
        }
      } catch (error) {
        console.error('Error checking first time user:', error)
        if (isMounted) {
          setIsFirstTime(false)
          setIsInTutorial(false)
        }
      }
    }

    checkFirstTimeUser()

    return () => {
      isMounted = false
    }
  }, [userData, walletAddress]) // Only depend on walletAddress

  const handleTabClick = useCallback((tabName: string) => {
    setActiveTab(tabName)
  }, [])

  const handleTutorialComplete = useCallback(() => {
    setIsFirstTime(false)
    setIsInTutorial(false)
  }, [])

  return (
    <BackendTokenContext.Provider value={{ token, setToken }}>
      <AppAuthProvider>
        <div className="tabs-container-wrapper">
          {isFirstTime && walletAddress && (
            <Tutorial
              onComplete={handleTutorialComplete}
              userId={walletAddress}
              activeTab={activeTab}
              onTabChange={handleTabClick}
            />
          )}

          <div className="tab-content">
            {activeTab === 'Cards' && <Cards />}
            {activeTab === 'Inventory' && (
              <Inventory
                isInTutorial={isInTutorial}
                defaultSection={isInTutorial ? 'packs' : 'fragments'}
                defaultExpanded={'unopened'}
              />
            )}
            {activeTab === 'Explore' && <Explore />}
            {activeTab === 'Store' && <Store />}
            {activeTab === 'Wallet' && <Wallet />}
          </div>

          {/* Tabs Navigation */}
          <div className="tabs-container">
            <div
              className={`tab-item ${activeTab === 'Cards' ? 'active' : ''}`}
              onClick={() => handleTabClick('Cards')}
            >
              <svg>
                <use href="#cards-icon" />
              </svg>
              <p>Cards</p>
            </div>

            <div
              className={`tab-item ${activeTab === 'Inventory' ? 'active' : ''}`}
              onClick={() => handleTabClick('Inventory')}
            >
              <svg>
                <use href="#inventory-icon" />
              </svg>
              <p>Inventory</p>
            </div>

            <div
              className={`tab-item ${activeTab === 'Explore' ? 'active' : ''}`}
              onClick={() => handleTabClick('Explore')}
            >
              <svg>
                <use href="#explore-icon" />
              </svg>
              <p>Explore</p>
            </div>

            <div
              className={`tab-item ${activeTab === 'Store' ? 'active' : ''}`}
              onClick={() => handleTabClick('Store')}
            >
              <svg>
                <use href="#store-icon" />
              </svg>
              <p>Store</p>
            </div>

            <div
              className={`tab-item ${activeTab === 'Wallet' ? 'active' : ''}`}
              onClick={() => handleTabClick('Wallet')}
            >
              <svg>
                <use href="#wallet-icon" />
              </svg>
              <p>Wallet</p>
            </div>
          </div>
        </div>
      </AppAuthProvider>
    </BackendTokenContext.Provider>
  )
}
